import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { Dataset } from "../types/api";
import { DatasetDetailPanel } from "./DatasetDetailPanel";
import { isWorkbookDataset } from "../utils/datasetDownload";

interface ProjectDatasetsSplitViewProps {
  projectId: number;
  projectTitle: string;
  datasets: Dataset[];
  onDownload: (ds: Dataset, format?: "native" | "csv") => Promise<void>;
  downloadingId: number | null;
  // Project managers additionally see whether a dataset is hidden from the
  // catalog. It never implies download rights: that comes from ds.can_download
  // alone, so the affordance matches what the download endpoint will allow.
  // Changing the hidden flag is not offered here — see the visibility badge below.
  canManage?: boolean;
  // An archived project is read-only: can_download already comes back false, so
  // the only affordance left to suppress is requesting access.
  archived?: boolean;
  uploaderNameById?: Record<number, string>;
}

const archivedHint =
  "This project is archived and read-only. Restore it in Settings -> Project Controls first.";

const isExpired = (ds: Dataset) =>
  ds.expires_at ? new Date(ds.expires_at) <= new Date() : false;
const isDownloadable = (ds: Dataset) => !!ds.can_download && !isExpired(ds);

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const DownloadIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 14,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1M7 10l5 5 5-5M12 15V3" />
  </svg>
);

const EyeIcon: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M9.36 9.36a3 3 0 0 0 4.28 4.28" />
    <path d="M6.6 6.62A13.53 13.53 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
    <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" />
    <path d="M2 2l20 20" />
  </svg>
);


export const ProjectDatasetsSplitView: React.FC<
  ProjectDatasetsSplitViewProps
> = ({
  projectId,
  projectTitle,
  datasets,
  onDownload,
  downloadingId,
  canManage = false,
  archived = false,
  uploaderNameById,
}) => {
  const navigate = useNavigate();
  // Nothing is open on arrival — the page is a list first. Clicking a row
  // opens it; clicking the open row closes it again.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Drop the open row if it disappears (deleted, or filtered out by a reload).
  useEffect(() => {
    if (selectedId != null && !datasets.some((d) => d.id === selectedId)) {
      setSelectedId(null);
    }
  }, [datasets, selectedId]);

  const toggleSelected = (id: number) =>
    setSelectedId((prev) => (prev === id ? null : id));

  const selected = datasets.find((d) => d.id === selectedId) || null;

  const checkedDownloadable = useMemo(
    () => datasets.filter((d) => checked.has(d.id) && isDownloadable(d)),
    [datasets, checked],
  );
  const checkedRequestable = useMemo(
    () => datasets.filter((d) => checked.has(d.id) && !isDownloadable(d)),
    [datasets, checked],
  );
  useEffect(() => {
    if (!selectAllRef.current) return;
    const n = checked.size;
    selectAllRef.current.checked = n > 0 && n === datasets.length;
    selectAllRef.current.indeterminate = n > 0 && n < datasets.length;
  }, [checked, datasets.length]);

  const toggleChecked = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setChecked((prev) =>
      prev.size === datasets.length
        ? new Set()
        : new Set(datasets.map((d) => d.id)),
    );
  };

  const requestAccess = (ds: Dataset) => {
    navigate("/requests/new", {
      state: { preselectedDataset: ds, preselectedProjectId: projectId },
    });
  };

  const handleBatchDownload = async () => {
    setBatchDownloading(true);
    try {
      // A single selected file downloads its own pseudonymized CSV directly;
      // multiple files bundle into one .zip (with loader scripts) via the
      // bulk-download endpoint.
      if (checkedDownloadable.length === 1) {
        await onDownload(checkedDownloadable[0]);
        return;
      }
      const blob = await apiService.bulkDownloadDatasets(
        projectId,
        checkedDownloadable.map((ds) => ds.id),
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `project_${projectId}_datasets.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBatchDownloading(false);
    }
  };

  if (datasets.length === 0) {
    return <div className="no-data">No datasets in this project yet.</div>;
  }

  return (
    <div className="ds-split">
      <style>{`
        .ds-split {
          display: grid;
          grid-template-columns: 320px 1fr;
          border: 0.5px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          min-height: 480px;
        }
        .ds-list-panel {
          background: var(--surface-1);
          border-right: 0.5px solid var(--border);
          padding: 0.85rem;
          overflow: auto;
        }
        .ds-select-all {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        .ds-select-all label {
          font-size: 12px;
          color: var(--text-secondary);
        }
        .ds-batch-actions {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 10px;
        }
        .ds-batch-btn {
          width: 100%;
          padding: 7px;
          border-radius: var(--radius);
          font-size: 12px;
          font-family: inherit;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          cursor: pointer;
          border: 0.5px solid var(--border-strong);
          background: var(--surface-2);
        }
        .ds-batch-btn:hover {
          background: var(--surface-1);
        }
        .ds-batch-btn:disabled {
          background: var(--fill-disabled);
          color: var(--text-disabled);
          border-color: var(--border-disabled);
          cursor: not-allowed;
        }
        .ds-batch-btn.primary {
          background: var(--fill-primary);
          color: var(--on-primary);
          border: none;
        }
        .ds-batch-note {
          margin: 0;
          font-size: 11px;
          line-height: 1.45;
          color: var(--text-muted);
        }
        .ds-visibility-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          padding: 3px 9px;
          border-radius: var(--radius);
          margin-bottom: 16px;
        }
        .ds-visibility-badge.visible {
          background: var(--bg-success);
          color: var(--text-success);
        }
        .ds-visibility-badge.hidden {
          background: var(--surface-1);
          color: var(--text-muted);
        }
        .ds-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          background: var(--surface-2);
        }
        .ds-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 9px;
          border-left: 2px solid transparent;
        }
        .ds-row:not(:last-child) {
          border-bottom: 0.5px solid var(--border);
        }
        .ds-row.selected {
          background: var(--bg-accent-muted);
          border-left-color: var(--border-accent);
        }
        .ds-row-body {
          flex: 1;
          min-width: 0;
          cursor: pointer;
        }
        .ds-row-name {
          font-size: 13px;
          font-weight: 500;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ds-row.selected .ds-row-name {
          color: var(--text-accent);
        }
        .ds-row-sub {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0;
        }
        .ds-row-noaccess {
          font-size: 10px;
          color: var(--text-warning);
          flex-shrink: 0;
        }
        .ds-detail-pane {
          padding: 1.5rem 2rem;
          overflow: auto;
        }
        .ds-detail-empty {
          margin: 0;
          font-size: 14px;
          color: var(--text-muted);
        }
        .ds-detail-desc {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0 0 20px;
          max-width: 560px;
        }
      `}</style>

      <div className="ds-list-panel">
        <div className="ds-select-all">
          <input
            ref={selectAllRef}
            type="checkbox"
            id="ds-select-all"
            style={{ width: "15px", height: "15px" }}
            onChange={toggleSelectAll}
          />
          <label htmlFor="ds-select-all">
            Select all · {datasets.length} datasets
          </label>
        </div>

        {(checkedDownloadable.length > 0 ||
          checkedRequestable.length > 0) && (
              <div className="ds-batch-actions">
                {checkedDownloadable.length > 0 && (
                  <button
                    className="ds-batch-btn primary"
                    onClick={handleBatchDownload}
                    disabled={batchDownloading}
                  >
                    <DownloadIcon />
                    {batchDownloading
                      ? "Downloading…"
                      : `Download (${checkedDownloadable.length})`}
                  </button>
                )}
                {/* A data request carries a single dataset_id, so access is
                    requested one dataset at a time. */}
                {checkedRequestable.length === 1 && (
                  <button
                    className="ds-batch-btn"
                    onClick={() => requestAccess(checkedRequestable[0])}
                    disabled={archived}
                    title={archived ? archivedHint : undefined}
                  >
                    Request access (1)
                  </button>
                )}
                {checkedRequestable.length > 1 && (
                  <p className="ds-batch-note">
                    Access is requested one dataset at a time — select a single
                    dataset, or use Request access in its detail view.
                  </p>
                )}
              </div>
            )}

        <div className="ds-list">
          {datasets.map((ds) => (
            <div
              key={ds.id}
              className={`ds-row ${selectedId === ds.id ? "selected" : ""}`}
            >
              <input
                type="checkbox"
                style={{ width: "14px", height: "14px", flexShrink: 0 }}
                checked={checked.has(ds.id)}
                onChange={() => toggleChecked(ds.id)}
              />
              <div
                className="ds-row-body"
                role="button"
                tabIndex={0}
                aria-expanded={selectedId === ds.id}
                onClick={() => toggleSelected(ds.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSelected(ds.id);
                  }
                }}
              >
                <p className="ds-row-name">{ds.display_name}</p>
                <p className="ds-row-sub">
                  {ds.row_count > 0
                    ? `${ds.row_count.toLocaleString()} rows`
                    : "—"}
                </p>
              </div>
              {canManage && ds.is_hidden ? (
                <span
                  title="Hidden from non-admin users"
                  style={{ flexShrink: 0, color: "var(--text-muted)" }}
                >
                  <EyeOffIcon />
                </span>
              ) : isDownloadable(ds) ? (
                <DownloadIcon color="var(--text-muted)" />
              ) : (
                <span className="ds-row-noaccess">
                  {isExpired(ds) ? "Expired" : "No access"}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="ds-detail-pane">
        {!selected && (
          <p className="ds-detail-empty">
            Select a dataset to see its columns and summary statistics.
          </p>
        )}
        {selected && (
          <DatasetDetailPanel
            dataset={selected}
            crumb={projectTitle}
            actionButton={
              isDownloadable(selected) ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    className="dd-action-btn"
                    onClick={() => onDownload(selected)}
                    disabled={downloadingId === selected.id}
                  >
                    <DownloadIcon size={16} />
                    {downloadingId === selected.id
                      ? "Downloading…"
                      : isWorkbookDataset(selected)
                        ? "Download workbook"
                        : "Download"}
                  </button>
                  {isWorkbookDataset(selected) && (
                    <button
                      className="dd-action-btn"
                      style={{ opacity: 0.85 }}
                      onClick={() => onDownload(selected, "csv")}
                      disabled={downloadingId === selected.id}
                      title="Flattened first sheet, supports selecting a column subset"
                    >
                      Download as CSV
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className="dd-action-btn"
                  onClick={() => requestAccess(selected)}
                  disabled={isExpired(selected) || archived}
                  title={archived ? archivedHint : undefined}
                >
                  {isExpired(selected)
                    ? "Expired"
                    : archived
                      ? "Project archived"
                      : "Request access"}
                </button>
              )
            }
          >
            {canManage ? (
              <>
                {/* Read-only. The old Hide/Unhide buttons were an all-or-none
                    toggle that sat awkwardly beside the two scopes set at
                    upload time (metadata_scope = who can discover,
                    download_scope = who can download). Controlling discovery
                    for named people needs a metadata_user_ids column and a
                    "selected_members" metadata scope, which don't exist yet —
                    so the flag is shown, not edited, until that lands. */}
                <span
                  className={`ds-visibility-badge ${selected.is_hidden ? "hidden" : "visible"}`}
                >
                  {selected.is_hidden ? <EyeOffIcon /> : <EyeIcon />}
                  {selected.is_hidden
                    ? "Hidden from non-admins"
                    : "Visible to project"}
                </span>
                <p className="ds-detail-desc">
                  Dataset uploaded by{" "}
                  {(selected.uploaded_by &&
                    uploaderNameById?.[selected.uploaded_by]) ||
                    "someone"}{" "}
                  on {formatDateTime(selected.created_at)}
                </p>
              </>
            ) : (
              selected.description && (
                <p className="ds-detail-desc">{selected.description}</p>
              )
            )}
          </DatasetDetailPanel>
        )}
      </div>
    </div>
  );
};
